import React, { PureComponent, RefObject } from 'react';
import styled, { withTheme } from 'styled-components';
// don't remove this import - hack to make fullcalendar work

import FullCalendar from '@fullcalendar/react';
import { Draggable } from '@fullcalendar/interaction';
import {
  CalendarEventType,
  Formats,
  PersonalizationSettings,
  PileOverdueTask,
  PileToDoTask,
  TaskState
} from 'shovel-lib/types';
import { formatMomentDateAs, now } from 'shovel-lib/utils/timeUtils';
import TaskCard from '../../task/TaskCard';
import { PlannedContainer } from '../../task/partials/PlannedTag';
import { cardDates } from '../../../utils/dayLabel';
import { stopDraggingTask } from '../../../state/calendar/actions';
import { DraggingTask } from '../../../state/calendar/types';
import { calendarStylingOptions, draggableStyle, extendDraggableItem } from '../../../utils/calendar/calendarUtils';
import { DraggableTask } from '../../common/layoutUtils';
import { Container, DeleteTaskIcon, MarkCompleteBtn } from '../../task/TaskCard.styles';
import MaterialIcon from '../../common/icons/MaterialIcon';
import { SmallText } from '../../../utils/typography';
import t from '../../../i18n/t';
import TippyTooltip from '../../common/tooltip/TippyTooltip';
import * as taskActions from '../../../state/task/actions';
import { calculateCushion } from '../../../state/pile/actions';
import TaskStatus from '../../task/TaskStatus';
import colors from '../../../utils/colors';
import { GreenSweep, sweepGreen } from '@utils/taskRowCompletion';
import { RootState } from '@state/types';
import { settingsSelectors } from '@state/settings';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

// don't remove this - hack to guarantee that fullcalendar core import will stay
const name = FullCalendar.name;

type Props = {
  userSettings: any;
  task: any;
  state: TaskState.COMPLETED | TaskState.ACTIVE | TaskState.DO_LATER;
  startDraggingTask?: any;
  stopDraggingTask?: typeof stopDraggingTask;
  draggingTask?: DraggingTask;
  className?: string;
  onPress: (clickData: any) => void;
  onMarkAsToDo?: any;
  onMarkAsComplete?: any;
  /** Not read here, but a change (date order, 12/24 hours) redraws the card's dates, which follow the app's locale. */
  settings?: PersonalizationSettings;
  openConfirmationDialog?: any;
  deleteTask?: any;
  openMoveToDoDialog?: any;
  isMonth?: boolean;
  theme: any;
  isPile?: boolean;
  /** Today and tomorrow by their weekday too ("Due: Tue"), in the calendar's popups (utils/dayLabel). */
  weekdayDates?: boolean;
  //for demo purpose
  isDisabledDragging?: boolean;
  /** For surfaces that keep their own copy of the task and have to refetch it after an estimate. */
  onEstimateSaved?: () => void;
  changePriority?: any;
  /** Works the Cushion out for the sidebar's list, from the card's grey Cushion icon. */
  calculateCushion: typeof calculateCushion.request;
};

type State = {
  animationActive: boolean;
  estimateOpen: boolean;
};

class PileTask extends PureComponent<Props, State> {
  state: State = {
    animationActive: false,
    estimateOpen: false
  };

  draggableRef: RefObject<any> = React.createRef();
  draggableTask: Draggable | null = null;
  // The element the drag is bound to: the card's own, which month view swaps out.
  draggableElement: HTMLElement | null = null;
  animateTimeout?: any = undefined;
  private completionPersisted = false;
  private sweep?: GreenSweep;

  isDraggable = () => {
    return !this.props.isMonth && (this.props.state === TaskState.ACTIVE || this.props.state === TaskState.DO_LATER);
  };

  canBindDraggableTask = () =>
    !this.props.isDisabledDragging &&
    !this.state.estimateOpen &&
    this.isDraggable() &&
    this.props.startDraggingTask &&
    this.props.stopDraggingTask &&
    !this.draggableTask;

  componentDidMount() {
    if (this.canBindDraggableTask()) {
      this.bindDraggableTask();
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevProps.isDisabledDragging && this.canBindDraggableTask()) {
      this.bindDraggableTask();
    }
    // A drag starting inside the estimate controls is never meant as a drag.
    if (prevState.estimateOpen !== this.state.estimateOpen) {
      if (this.state.estimateOpen) {
        this.draggableTask?.destroy();
        this.draggableTask = null;
        this.draggableElement = null;
      } else if (this.canBindDraggableTask()) {
        this.bindDraggableTask();
      }
    }
    // The card's element changed (month view and back): the drag follows it.
    if (this.draggableRef.current !== this.draggableElement && !this.state.estimateOpen) {
      this.draggableTask?.destroy();
      this.draggableTask = null;
      this.draggableElement = null;
      if (this.draggableRef.current && this.canBindDraggableTask()) this.bindDraggableTask();
    }
    // The drag reads the task as it is when it starts (bindDraggableTask), so a change to it never rebuilds the drag:
    // rebuilt mid-drag, the card never dropped and had to be grabbed again (Petr, 2026-10-16: unplanning a session
    // changes its task's planned time twice, once on the save and again as the list reloads, often while dragging it).
    const { timePlanned, timeNeed } = this.props.task as PileOverdueTask | PileToDoTask;
    const { timePlanned: prevPlanned, timeNeed: prevNeed } = prevProps.task as PileOverdueTask | PileToDoTask;
    if (timePlanned !== prevPlanned || (timeNeed !== prevNeed && this.draggableRef.current)) {
      this.draggableRef.current?.classList?.add?.('animating');
      if (!!this.animateTimeout) {
        clearTimeout(this.animateTimeout);
      }
      this.animateTimeout = setTimeout(() => {
        if (this.draggableRef.current) {
          this.draggableRef.current.classList.remove('animating');
        }
      }, 3000);
    }
  }

  bindDraggableTask = () => {
    this.draggableElement = this.draggableRef.current;
    this.draggableTask = new Draggable(this.draggableRef.current, {
      eventData: () => {
        const { isMonth, theme, userSettings } = this.props;
        const { taskId, colorHex, type, due, title, timeNeed, timePlanned, timeSpent, courseName } = this.props.task as
          | PileOverdueTask
          | PileToDoTask;
        const timeUnplanned = timeNeed - timePlanned;
        const noEstimation = timeNeed + timeSpent === 0;
        const duration = noEstimation || timeUnplanned <= 0 || timeUnplanned > 60 ? 60 : timeUnplanned;
        const courseColor = colorHex || colors.randomTaskColor;
        const backgroundColor = calendarStylingOptions(theme)[userSettings.plannedTaskEventStyle.style].backgroundFunction(
          courseColor
        );
        return {
          taskId: taskId,
          type: CalendarEventType.PLANNED_TASK,
          taskType: type,
          taskColor: colorHex,
          due,
          title,
          duration: isMonth ? undefined : duration * 60000, // duration in milliseconds
          textColor: courseColor,
          colorHex,
          borderColor: theme.commitmentBorderFunction(courseColor),
          backgroundColor,
          courseColor,
          courseName: courseName || t.RANDOM,
          classNames: ['planned-task-event']
        };
      },
      longPressDelay: 200
    });
    extendDraggableItem(this.draggableTask);
    // TODO: check this implementation
    // @ts-ignore
    const draggableTaskHandlers = this.draggableTask.dragging.emitter.handlers;

    draggableTaskHandlers.dragstart.push(() => {
      const { taskId, due } = this.props.task as PileOverdueTask | PileToDoTask;
      this.props.startDraggingTask!({ taskId, dueDate: due });
    });

    draggableTaskHandlers.dragend.push(() => {
      this.props.stopDraggingTask!();
    });
  };

  render() {
    const { isDisabledDragging } = this.props;

    return this.isDraggable() && !isDisabledDragging ? (
      // draggable is switched off rather than the wrapper swapped out: swapping it unmounts the card, which
      // would throw away the open estimate panel's state on the very click that opened it.
      <DraggableTaskWrapper ref={this.draggableRef} className={this.props.className} draggable={!this.state.estimateOpen}>
        {this.renderTask()}
      </DraggableTaskWrapper>
    ) : (
      this.renderTask()
    );
  }

  renderTask = () => {
    const {
      onPress,
      task,
      state,
      isPile = true,
      isDisabledDragging,
      className,
      userSettings,
      theme,
      changePriority
    } = this.props;
    const {
      taskId,
      due,
      type,
      timeNeed,
      cushion,
      completedOn,
      courseName,
      title,
      subtitle,
      colorHex,
      duration,
      categoryName,
      categoryId,
      courseId,
      emoji,
      readingSourceName,
      timePlanned,
      timeSpent,
      priority,
      daysToStartAhead
    } = task;

    // Completed cards keep the plain date (history); the rest read "Due: Thu 10:00", "Start: Tomorrow" (utils/dayLabel).
    const dates = cardDates(due, daysToStartAhead, { weekdays: this.props.weekdayDates });
    const duePreview =
      due && (state === TaskState.COMPLETED ? formatMomentDateAs(due, Formats.DATE_TIME_FORMAT) : dates.due);
    const startPreview = state === TaskState.COMPLETED ? '' : dates.start;

    let isDragging = false;
    if (this.isDraggable()) {
      const { draggingTask } = this.props;
      isDragging = !!draggingTask && draggingTask.taskId === taskId;
    }

    const hasSidebar = userSettings.plannedTaskEventStyle.sidebar;
    const backgroundColor = calendarStylingOptions(theme)[userSettings.plannedTaskEventStyle.style].backgroundFunction(
      colorHex || colors.randomTaskColor
    );

    const Card = this.isDraggable() ? DraggableTaskCard : TaskCard;
    //this is added because of demo task card animation
    if (isDisabledDragging) {
      return (
        <TaskWrapper className={className}>
          <DemoTaskCard
            hasSidebar={hasSidebar}
            backgroundColor={backgroundColor}
            className={`pile-task`}
            due={duePreview}
            start={startPreview}
            colorHex={colorHex}
            type={type}
            duration={duration}
            cushionComponent={
              <TaskStatus
                {...{ state, timeNeed, cushion, completedOn, isPile, due }}
                onCalculate={isPile ? this.props.calculateCushion : undefined}
              />
            }
            timeNeed={timeNeed}
            timePlanned={timePlanned}
            timeSpent={timeSpent}
            course={courseName}
            title={title}
            subtitle={subtitle}
            onClick={onPress}
            isPile={isPile}
            isCompleted={state === TaskState.COMPLETED}
            renderMarkBtn={this.renderMarkBtn}
            renderDeleteBtn={this.renderDeleteBtn}
            categoryName={categoryName}
            categoryEmoji={emoji}
            readingSourceName={readingSourceName}
            hasNoActions={!isPile}
          />
        </TaskWrapper>
      );
    }
    const taskElement = (
      <Card
        hasSidebar={hasSidebar}
        backgroundColor={backgroundColor}
        className={`pile-task ${isDragging ? 'dragged-pile-task' : ''}`}
        due={duePreview}
        start={startPreview}
        colorHex={colorHex || colors.randomTaskColor}
        type={type}
        duration={duration}
        cushionComponent={
          <TaskStatus
            {...{ state, timeNeed, cushion, completedOn, isPile, due }}
            onCalculate={isPile ? this.props.calculateCushion : undefined}
          />
        }
        timeNeed={timeNeed}
        timePlanned={timePlanned}
        timeSpent={timeSpent}
        course={courseName || t.RANDOM}
        title={title}
        subtitle={subtitle}
        onClick={onPress}
        isPile={isPile}
        isCompleted={state === TaskState.COMPLETED}
        renderMarkBtn={this.renderMarkBtn}
        renderDeleteBtn={this.renderDeleteBtn}
        categoryName={categoryName}
        categoryEmoji={emoji}
        categoryId={categoryId}
        courseId={courseId}
        readingSourceName={readingSourceName}
        taskId={taskId}
        canEstimate
        onEstimateSaved={this.props.onEstimateSaved}
        onEstimateOpenChange={estimateOpen => this.setState({ estimateOpen })}
        hasNoActions={!isPile}
        dragDisabled={!this.isDraggable()}
        priority={priority || 0}
        changePriority={changePriority}
      />
    );
    return taskElement;
  };

  onMarkToDoClick = () => {
    const { task, onMarkAsToDo, openMoveToDoDialog, state } = this.props;
    if (state !== TaskState.COMPLETED) return;
    if (now().isAfter(this.props.task.due)) {
      openMoveToDoDialog({ dueDate: task.due, daysAhead: task.daysToStartAhead, taskId: task.taskId });
    } else {
      const data = { dueDate: task.due && task.due.toISOString(), daysAhead: task.daysToStartAhead };
      onMarkAsToDo({ taskId: task.taskId, data });
    }
  };

  /**
   * Green sweeps across the card and sparkles burst from its button, as in the Task List; once the card has faded it
   * leaves the list, the cards below slide up, and it is saved (`onMarkAsComplete`, Pile). It replaced a particle
   * effect drawn frame by frame, which got choppy and ran long on a slow computer, ran fast on a 120 Hz screen, and held
   * up the save while it played (Petr).
   */
  onMarkComplete = (button: Element) => {
    const { state } = this.props;
    if (state === TaskState.COMPLETED || this.state.animationActive) return;
    this.setState({ animationActive: true });
    const card = button.closest<HTMLElement>('.pile-task');
    // As the green reaches the card's right side, a quick shot of streaks out of that side, like a muzzle flash
    // (Petr) - not a burst from the button, which is gone the moment it's clicked ("sparkles in an empty spot"), nor
    // up from the corner. The sidebar sits at the window's edge, so they fly only as far as there's room.
    const box = card?.getBoundingClientRect();
    const room = box ? window.innerWidth - box.right : 0;
    this.sweep =
      card && box
        ? sweepGreen(card, {
            x: box.right - 2,
            y: box.top + box.height / 2,
            aim: 0,
            // Out of the whole side, the card's height (Petr), each one nearly straight out.
            along: box.height - 8,
            spread: 0.35,
            reach: [Math.max(10, Math.min(room - 6, 28) * 0.6), Math.max(14, Math.min(room - 4, 40))],
            duration: 320,
            count: 14,
            streaks: true
          })
        : undefined;
    (this.sweep?.faded ?? Promise.resolve()).then(this.persistCompletion);
  };

  persistCompletion = () => {
    const { state, onMarkAsComplete, task } = this.props;
    if (this.completionPersisted || state === TaskState.COMPLETED || !onMarkAsComplete) return;
    this.completionPersisted = true;
    onMarkAsComplete({ taskId: task.taskId, data: { state: TaskState.COMPLETED } });
  };

  componentWillUnmount() {
    if (this.animateTimeout) {
      clearTimeout(this.animateTimeout);
    }
    // If the card unmounts mid-animation (navigate away), still persist completion.
    if (this.state.animationActive) {
      this.persistCompletion();
    }
    this.sweep?.clear();
    if (this.draggableTask) {
      this.draggableTask.destroy();
      this.draggableTask = null;
    }
  }

  // `highlight`: the task's time is all used, so completing it is the likely next step.
  renderMarkBtn = (highlight?: boolean) => {
    const { state } = this.props;
    if (state !== TaskState.COMPLETED) {
      return (
        <TippyTooltip
          target={
            <MarkCompleteBtn
              $highlight={highlight}
              onClick={e => {
                this.onMarkComplete(e.currentTarget);
                e.stopPropagation();
              }}
            >
              <MaterialIcon name={'done'} size={16} style={{ marginRight: '2px' }} />
              <SmallText>{t.MARK_COMPLETE}</SmallText>
            </MarkCompleteBtn>
          }
          content={t.MARK_COMPLETE_TOOLTIP}
          width="170px"
        />
      );
    }

    return (
      <MarkCompleteBtn
        onClick={e => {
          this.onMarkToDoClick();
          e.stopPropagation();
        }}
      >
        <MaterialIcon name={'undo'} size={16} style={{ marginRight: '2px' }} />
        <SmallText>{t.MARK_TO_DO}</SmallText>
      </MarkCompleteBtn>
    );
  };

  onDeleteTask = () => {
    const dummyAction = taskActions.deleteTask; // don't remove - impacts the modules creation order and app crashes

    const { deleteTask, task } = this.props;
    deleteTask({ id: task.taskId });
  };

  renderDeleteBtn = () => {
    return (
      <TippyTooltip
        target={
          <div style={{ height: '16px' }}>
            <DeleteTaskIcon
              name={'delete_outline'}
              size={16}
              onClick={e => {
                this.onDeleteTask();
                e.stopPropagation();
              }}
            />
          </div>
        }
        content={t.DELETE_TASK}
      />
    );
  };
}

const mapStateToProps = (state: RootState) => ({ userSettings: settingsSelectors.getUserSettings(state) });

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ calculateCushion: calculateCushion.request }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(PileTask));

const DraggableTaskWrapper = styled.div`
  &.animating ${PlannedContainer} {
    animation: pulse-planned 1s ease-out infinite;
    @keyframes pulse-planned {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.25);
      }
      100% {
        transform: scale(1);
      }
    }
  }
  & ${DraggableTask} {
    display: none;
  }

  .pile-task {
    transition: opacity 0.2s ease;
  }

  &.agenda-dragging.agenda-drop-not-allowed .pile-task {
    opacity: 0.9;
  }

  &.fc-dragging .pile-task {
    display: none;
  }

  &.agenda-dragging.agenda-drop-allowed .pile-task {
    opacity: 0;
  }

  &.agenda-dragging ${DraggableTask}, &.fc-dragging ${DraggableTask} {
    display: flex;
  }
`;

const TaskWrapper = styled.div``;

const DraggableTaskCard = styled(TaskCard)`
  ${draggableStyle}
`;

const DemoTaskCard = styled(TaskCard)`
  cursor: default !important;
`;
